// src/components/openDialog/EditRelaxationDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { updateRelaxation } from '../../services/relaxation/updateRelaxation';

const CATEGORIES = [
  'Nature Sounds',
  'Guided Relaxation',
  'Ambient Music',
  'Sound Healing',
  'White / Brown Noise',
  'Binaural Beats',
  'ASMR',
  'Meditation',
  'Sleep Story',
  'Yoga / Savasana',
];

const EditRelaxationDialog = ({ open, onClose, relaxation, onSave }) => {
  const queryClient = useQueryClient();
  const { courseid } = useParams();

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    duration: '',
    instructions: '',
    benefits: '',
    audioFile: null,
    existingAudioUrl: '',
  });

  const [uploading, setUploading] = useState(false);
  const [removingAudio, setRemovingAudio] = useState(false);

  // ✅ Fill form correctly
  useEffect(() => {
    if (open && relaxation) {
      setForm({
        name: relaxation.track_name || '',
        description: relaxation.short_description || '',
        category: relaxation.category || '',
        duration: relaxation.duration || '',
        instructions: relaxation.instructions || '',
        benefits: Array.isArray(relaxation.benefits)
          ? relaxation.benefits.join('\n')
          : relaxation.benefits || '',
        audioFile: null,
        existingAudioUrl: relaxation.audio_file
          ? `${import.meta.env.VITE_APP_API_URL}/${relaxation.audio_file}`
          : '',
      });
    }
  }, [open, relaxation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file');
      return;
    }

    setForm((prev) => ({ ...prev, audioFile: file }));
  };

  // ✅ MUTATION
  const updateMutation = useMutation({
    mutationFn: updateRelaxation,
    onSuccess: () => {
      toast.success('Relaxation updated successfully');
      queryClient.invalidateQueries({ queryKey: ['relaxations', courseid] });
      onSave?.();
      onClose();
    },
    onError: (err) => {
      toast.error(err?.message || 'Update failed');
    },
  });

  // ✅ HANDLE SUBMIT (perfectly aligned with Joi)
  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Track name is required');
      return;
    }

    if (!form.category) {
      toast.error('Please select a category');
      return;
    }

    setUploading(true);

    try {
      const payload = {
        id: relaxation.id,
      };

      if (form.name.trim()) {
        payload.trackName = form.name.trim();
      }

      if (form.category) {
        payload.category = form.category;
      }

      if (form.duration) {
        payload.duration = form.duration;
      }

      if (form.description.trim()) {
        payload.shortDescription = form.description.trim();
      }

      if (form.instructions.trim()) {
        payload.instructions = form.instructions.trim();
      }

      if (form.benefits.trim()) {
        payload.benefits = form.benefits
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
      }

      if (form.audioFile) {
        payload.audioFile = form.audioFile.name;
      }

      console.log("Sending update payload:", payload);

      await updateMutation.mutateAsync(payload);
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAudio = async () => {
    if (!form.existingAudioUrl) return;
    if (!window.confirm('Remove the current audio file?')) return;

    setRemovingAudio(true);

    try {
      setForm((prev) => ({
        ...prev,
        existingAudioUrl: '',
        audioFile: null,
      }));

      toast.success('Audio removed');
      queryClient.invalidateQueries({ queryKey: ['relaxations', courseid] });
    } catch (err) {
      toast.error('Failed to remove audio');
    } finally {
      setRemovingAudio(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Edit Relaxation Audio
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} mt={1}>

          <TextField
            label="Track Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            required
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={form.category}
                label="Category"
                onChange={handleChange}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Duration"
              name="duration"
              value={form.duration}
              onChange={handleChange}
              placeholder="10:30"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Format: minutes:seconds"
            />
          </Stack>

          {/* AUDIO FILE SECTION */}
<Box>
  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
    Audio File
  </Typography>

  {form.existingAudioUrl ? (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <audio controls src={form.existingAudioUrl} style={{ width: '100%' }} />
      <IconButton
        color="error"
        size="small"
        onClick={handleRemoveAudio}
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      No file currently attached
    </Typography>
  )}

  <Button
    component="label"
    variant="outlined"
    startIcon={<UploadIcon />}
    fullWidth
    sx={{ justifyContent: 'flex-start', py: 1.5 }}
  >
    {form.audioFile
      ? `Replace with: ${form.audioFile.name}`
      : 'Upload New / Replacement File'}
    <input
      type="file"
      hidden
      onChange={handleFileChange}
    />
  </Button>

  {form.audioFile && (
    <Typography
      variant="caption"
      color="primary"
      sx={{ mt: 1, display: 'block' }}
    >
      New file selected — will replace current file on save
    </Typography>
  )}
</Box>


          <TextField
            label="Instructions / Script"
            name="instructions"
            value={form.instructions}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
          />

          <TextField
            label="Benefits (one per line)"
            name="benefits"
            value={form.benefits}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label="Short Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />

        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={uploading || updateMutation.isPending}
        >
          {uploading || updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRelaxationDialog;
